import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Full-screen, non-interactive hint shown at the very start of a run.
 * - Centered text in Karma font
 * - White text with a soft golden glow
 * - Slight, irregular golden pulse
 * - A few subtle golden particles drifting out of the text
 * - pointer-events: none so the Torch icon remains clickable under it
 */
export default function TorchHintOverlay({ active, children, fadeMs = 220, contentKey = 'default', contentFadeMs = 180 }) {

  const [renderedChildren, setRenderedChildren] = useState(children);
  const [renderedContentKey, setRenderedContentKey] = useState(contentKey);
  const [isContentVisible, setIsContentVisible] = useState(true);
  const swapTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (swapTimeoutRef.current) {
        clearTimeout(swapTimeoutRef.current);
        swapTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!active) {
      if (swapTimeoutRef.current) {
        clearTimeout(swapTimeoutRef.current);
        swapTimeoutRef.current = null;
      }
      setRenderedChildren(children);
      setRenderedContentKey(contentKey);
      setIsContentVisible(true);
      return;
    }

    if (renderedContentKey === contentKey) {
      setRenderedChildren(children);
      return;
    }

    if (swapTimeoutRef.current) {
      clearTimeout(swapTimeoutRef.current);
      swapTimeoutRef.current = null;
    }

    setIsContentVisible(false);

    swapTimeoutRef.current = setTimeout(() => {
      setRenderedChildren(children);
      setRenderedContentKey(contentKey);
      setIsContentVisible(true);
      swapTimeoutRef.current = null;
    }, contentFadeMs);
  }, [active, children, contentKey, contentFadeMs, renderedContentKey]);

  const particles = useMemo(
    () =>
      Array.from({ length: 48 }, (_, index) => {
        const angleDeg = -112 + Math.random() * 44;
        const distancePx = 18 + Math.random() * 70;
        const driftX = Math.cos((angleDeg * Math.PI) / 180) * distancePx;
        const driftY = Math.sin((angleDeg * Math.PI) / 180) * distancePx;
        return {
          id: index,
          left: `${15 + Math.random() * 70}%`,
          top: `${42 + Math.random() * 18}%`,
          size: `${2 + Math.random() * 3.2}px`,
          duration: `${2.8 + Math.random() * 2.6}s`,
          delay: `${Math.random() * 2.4}s`,
          opacity: `${0.18 + Math.random() * 0.35}`,
          driftX: `${driftX.toFixed(2)}px`,
          driftY: `${driftY.toFixed(2)}px`,
          blur: `${0.2 + Math.random() * 1.2}px`,
          scaleMid: (1.25 + Math.random() * 0.65).toFixed(2),
        };
      }),
    []
  );

  return (
    <div
      className="dd-torch-hint"
      style={{
        opacity: active ? 1 : 0,
      }}
      aria-hidden="true"
    >
      <div
        className="dd-torch-hint__shell"
        style={{
          opacity: isContentVisible ? 1 : 0,
          transition: `opacity ${contentFadeMs}ms ease`,
        }}
      >
        <div className="dd-torch-hint__aura" />

        {particles.map((particle) => (
          <span
            key={particle.id}
            className="dd-torch-hint__particle"
            style={{
              left: particle.left,
              top: particle.top,
              width: particle.size,
              height: particle.size,
              ["--dd-particle-opacity"]: particle.opacity,
              animationDuration: particle.duration,
              animationDelay: particle.delay,
              filter: `blur(${particle.blur})`,
              ["--dd-particle-drift-x"]: particle.driftX,
              ["--dd-particle-drift-y"]: particle.driftY,
              ["--dd-particle-scale-mid"]: particle.scaleMid,
            }}
          />
        ))}

        <div className="dd-torch-hint__text">
          {renderedChildren ?? (
            <>
              Darkness will <b>consume</b> you.
              <br />
              Light up a torch.
            </>
          )}
        </div>
      </div>

      <style>{`
        .dd-torch-hint{
          position: fixed;
          inset: 0;
          z-index: 80;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          transition: opacity ${fadeMs}ms ease;
        }

        .dd-torch-hint__shell{
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 18px 28px;
        }

        .dd-torch-hint__aura{
          position: absolute;
          left: 50%;
          top: 50%;
          width: min(72vw, 760px);
          height: min(24vw, 230px);
          transform: translate(-50%, -50%);
          border-radius: 999px;
          opacity: 0.34;
          background:
            radial-gradient(circle at center,
              rgba(255, 207, 84, 0.22) 0%,
              rgba(255, 183, 46, 0.14) 28%,
              rgba(170, 96, 0, 0.08) 52%,
              rgba(0, 0, 0, 0) 74%);
          filter: blur(20px);
          animation: ddTorchHintAuraPulse 6.2s ease-in-out infinite;
        }

        .dd-torch-hint__text{
          position: relative;
          z-index: 2;
          font-family: 'Karma', serif;
          font-weight: 700;
          text-align: center;
          line-height: 1.15;
          letter-spacing: 0.02em;
          padding: 0 22px;
          font-size: clamp(22px, 3.4vw, 46px);
          color: rgba(255, 250, 238, 0.985);
          will-change: text-shadow, filter, opacity;
          animation:
            ddTorchHintPulse 5.8s ease-in-out infinite,
            ddTorchHintFlicker 7.1s linear infinite;
          text-shadow:
            0 0 6px rgba(255, 210, 92, 0.92),
            0 0 16px rgba(255, 186, 28, 0.56),
            0 0 28px rgba(176, 102, 0, 0.32);
        }

        .dd-torch-hint__text p{
          margin: 0.18em 0 0;
        }

        .dd-torch-hint__text b{
          color: rgba(255, 233, 170, 0.99);
        }

        .dd-torch-hint__particle{
          position: absolute;
          z-index: 1;
          border-radius: 999px;
          background:
            radial-gradient(circle at center,
              rgba(255, 244, 196, 0.98) 0%,
              rgba(255, 214, 100, 0.92) 38%,
              rgba(255, 172, 26, 0.35) 72%,
              rgba(255, 172, 26, 0) 100%);
          box-shadow: 0 0 8px rgba(255, 196, 70, 0.42);
          transform: translate3d(0, 0, 0) scale(0.7);
          animation-name: ddTorchHintParticleFloat;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          will-change: transform, opacity;
        }

        @keyframes ddTorchHintPulse {
          0% {
            filter: brightness(1);
            text-shadow:
              0 0 6px rgba(255, 210, 92, 0.92),
              0 0 16px rgba(255, 186, 28, 0.56),
              0 0 28px rgba(176, 102, 0, 0.32);
          }
          9% {
            filter: brightness(1.03);
            text-shadow:
              0 0 8px rgba(255, 216, 108, 0.96),
              0 0 18px rgba(255, 193, 38, 0.62),
              0 0 30px rgba(188, 112, 0, 0.34);
          }
          18% {
            filter: brightness(1);
            text-shadow:
              0 0 6px rgba(255, 210, 92, 0.92),
              0 0 16px rgba(255, 186, 28, 0.56),
              0 0 28px rgba(176, 102, 0, 0.32);
          }
          36% {
            filter: brightness(1.06);
            text-shadow:
              0 0 10px rgba(255, 223, 126, 1),
              0 0 22px rgba(255, 198, 52, 0.7),
              0 0 36px rgba(196, 118, 0, 0.4);
          }
          46% {
            filter: brightness(1.02);
          }
          63% {
            filter: brightness(1);
          }
          76% {
            filter: brightness(1.045);
            text-shadow:
              0 0 8px rgba(255, 216, 110, 0.97),
              0 0 19px rgba(255, 190, 38, 0.62),
              0 0 31px rgba(186, 110, 0, 0.35);
          }
          100% {
            filter: brightness(1);
            text-shadow:
              0 0 6px rgba(255, 210, 92, 0.92),
              0 0 16px rgba(255, 186, 28, 0.56),
              0 0 28px rgba(176, 102, 0, 0.32);
          }
        }

        @keyframes ddTorchHintFlicker {
          0%, 15%, 33%, 34%, 58%, 77%, 100% { opacity: 0.985; }
          16% { opacity: 0.965; }
          35% { opacity: 0.995; }
          59% { opacity: 0.972; }
          78% { opacity: 0.992; }
        }

        @keyframes ddTorchHintAuraPulse {
          0%, 100% {
            transform: translate(-50%, -50%) scale(0.98);
            opacity: 0.28;
          }
          32% {
            transform: translate(-50%, -50%) scale(1.03);
            opacity: 0.4;
          }
          68% {
            transform: translate(-50%, -50%) scale(1.01);
            opacity: 0.34;
          }
        }

        @keyframes ddTorchHintParticleFloat {
          0% {
            transform: translate3d(0, 4px, 0) scale(0.65);
            opacity: 0;
          }
          18% {
            opacity: var(--dd-particle-opacity, 0.28);
          }
          58% {
            transform: translate3d(
              calc(var(--dd-particle-drift-x) * 0.58),
              calc(var(--dd-particle-drift-y) * 0.58),
              0
            ) scale(var(--dd-particle-scale-mid, 1.45));
            opacity: var(--dd-particle-opacity, 0.28);
          }
          100% {
            transform: translate3d(
              var(--dd-particle-drift-x),
              var(--dd-particle-drift-y),
              0
            ) scale(0.38);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
